/**
 * taxRates.ts — Tasas de impuesto por producto (IVA, Impoconsumo, exento, personalizado).
 *
 * QUÉ EXPORTA: presets, validación y etiquetas para inventario, caja y tickets.
 * QUÉ NO DEBE HACER: Persistir datos ni calcular totales de venta.
 */

export interface TaxRatePreset {
  value: string;
  label: string;
}

/** Tarifas frecuentes en Colombia (no exhaustivas; el dueño puede usar % personalizado). */
export const TAX_RATE_PRESETS: readonly TaxRatePreset[] = [
  { value: "19", label: "IVA 19%" },
  { value: "8", label: "Impoconsumo 8%" },
  { value: "5", label: "IVA 5%" },
  { value: "0", label: "Exento 0%" },
] as const;

export const TAX_PRESET_VALUES = TAX_RATE_PRESETS.map((p) => p.value);

export function isTaxPresetValue(value: string): boolean {
  return TAX_PRESET_VALUES.includes(value);
}

export function clampTaxRatePct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

export function parseTaxRateInput(raw: string): number {
  const n = Number(raw);
  return clampTaxRatePct(Number.isNaN(n) ? 0 : n);
}

/** Nombre del impuesto según tarifa (para tickets y desglose fiscal). */
export function taxKindLabel(ratePct: number): string {
  if (ratePct === 8) return "Impoconsumo";
  if (ratePct === 0) return "Exento";
  return "IVA";
}

/** Línea informativa bajo un ítem en el ticket. El PVP ya incluye el impuesto. */
export function formatTicketItemTaxLine(ratePct: number): string {
  const kind = taxKindLabel(ratePct);
  if (ratePct === 0) return "Exento";
  return `${kind} ${ratePct}% (incluido)`;
}

/** Etiqueta de bucket en totales del ticket (Base gravable / desglose). */
export function formatTicketTaxBucketLabel(ratePct: number): string {
  const kind = taxKindLabel(ratePct);
  if (ratePct === 0) return "Exento";
  // «incluido» deja claro que NO se suma al total: el PVP ya lleva IVA dentro.
  return `${kind} incluido (${ratePct}%)`;
}
